import React, { useEffect, useRef } from "react";
import type { Feature } from "geojson";
import OpenLayersMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { Style, Stroke, Fill, Text } from "ol/style";
import { isEmpty as isEmptyExtent } from "ol/extent";

interface IProps {
  features: Feature[];
  height?: number;
}

const boundaryStyle = (label: string) =>
  new Style({
    stroke: new Stroke({ color: "#68ad18", width: 2 }),
    fill: new Fill({ color: "rgba(104, 173, 24, 0.2)" }),
    text: label
      ? new Text({
          text: label,
          font: "12px sans-serif",
          fill: new Fill({ color: "#1f2937" }),
          stroke: new Stroke({ color: "rgba(255,255,255,0.9)", width: 3 }),
          overflow: false,
        })
      : undefined,
  });

/** Small read-only map showing the boundaries about to be imported. */
const BoundaryPreviewMap: React.FC<IProps> = ({ features, height = 260 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<OpenLayersMap | null>(null);

  useEffect(() => {
    if (!containerRef.current || features.length === 0) return;

    const format = new GeoJSON();
    const olFeatures = format.readFeatures(
      { type: "FeatureCollection", features },
      { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" }
    );
    const source = new VectorSource({ features: olFeatures });

    const map = new OpenLayersMap({
      target: containerRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({
          source,
          style: (f) => boundaryStyle(String(f.get("name") ?? "")),
        }),
      ],
      view: new View({ center: [0, 0], zoom: 2 }),
      controls: [],
    });
    mapRef.current = map;

    const extent = source.getExtent();
    if (!isEmptyExtent(extent)) {
      map.getView().fit(extent, { padding: [24, 24, 24, 24], maxZoom: 16 });
    }

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, [features]);

  if (features.length === 0) return null;

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
      }}
    />
  );
};

export default BoundaryPreviewMap;
